import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export default function Programs() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('preschool');
  const [contentKey, setContentKey] = useState(0);
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setContentKey((prev) => prev + 1);
  };

  const tabs = [
    { id: 'preschool', label: t('programs.preschool'), icon: 'ri-bear-smile-line', age: t('programs.ages.preschool') },
    { id: 'primary', label: t('programs.primary'), icon: 'ri-book-2-line', age: t('programs.ages.primary') },
    { id: 'secondary', label: t('programs.secondary'), icon: 'ri-school-line', age: t('programs.ages.secondary') },
  ];

  const programDetails = {
    preschool: {
      title: t('programs.preschool'),
      description: t('programs.preschoolDesc'),
      image: 'https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/473a9190-fc2d-46a4-8aba-5b7c1e6c7e96_compressed_Petunia_PreescolarF.webp',
      methodology: t('programs.preschoolMethodology', { returnObjects: true }) as unknown as string[],
      benefits: t('programs.preschoolBenefits', { returnObjects: true }) as unknown as string[],
      activities: t('programs.preschoolActivities', { returnObjects: true }) as unknown as string[],
    },
    primary: {
      title: t('programs.primary'),
      description: t('programs.primaryDesc'),
      image: 'https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/b10c6434-7ec2-432f-b1b1-8a023149f027_compressed_Petunia_PrimariaF.webp',
      methodology: t('programs.primaryMethodology', { returnObjects: true }) as unknown as string[],
      benefits: t('programs.primaryBenefits', { returnObjects: true }) as unknown as string[],
      activities: t('programs.primaryActivities', { returnObjects: true }) as unknown as string[],
    },
    secondary: {
      title: t('programs.secondary'),
      description: t('programs.secondaryDesc'),
      image: 'https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/45f99ac9-f210-4bf9-844c-9bf3c0b23deb_compressed_Petunia_SecundariaF.webp',
      methodology: t('programs.secondaryMethodology', { returnObjects: true }) as unknown as string[],
      benefits: t('programs.secondaryBenefits', { returnObjects: true }) as unknown as string[],
      activities: t('programs.secondaryActivities', { returnObjects: true }) as unknown as string[],
    },
  };

  const current = programDetails[activeTab as keyof typeof programDetails];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[400px] md:h-[560px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/6a5f44cc-4a47-4fc3-a774-d6e924303bf5_compressed_Programas_academicosF_comprimida.webp"
            alt={t('programs.title')}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-purple-900/60 via-purple-900/50 to-purple-900/70" />
        </div>
        <div className="relative z-10 text-center px-4">
          <span className="text-yellow-400 text-xs font-semibold tracking-[0.2em] uppercase">{t('programs.heroBadge')}</span>
          <h1 className="font-serif text-4xl md:text-6xl font-bold text-white mt-4 leading-tight">{t('programs.title')}</h1>
          <p className="text-white/70 mt-4 max-w-2xl mx-auto text-base leading-relaxed">{t('programs.subtitle')}</p>
        </div>
      </section>

      {/* Tabs */}
      <section id="tabs" data-animate className="py-20 md:py-28 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
          {/* Tab Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-16" role="tablist" aria-label={t('programs.title')}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`flex items-center gap-3 px-6 py-4 rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap w-full sm:w-auto justify-center cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-purple-800 text-white shadow-lg shadow-purple-800/20'
                    : 'bg-purple-50 text-purple-800 hover:bg-white hover:shadow-md hover:shadow-purple-800/10 border border-purple-100 hover:-translate-y-0.5'
                }`}
              >
                <i className={`${tab.icon} text-lg`} />
                <div className="text-left">
                  <div className="text-sm">{tab.label}</div>
                  <div className={`text-xs font-normal ${activeTab === tab.id ? 'text-white/70' : 'text-black/30'}`}>{tab.age}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Content */}
          <div key={contentKey} ref={contentRef} className="animate-tab-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center mb-16">
              <div className={`rounded-2xl overflow-hidden transition-all duration-700 bg-white flex items-center justify-center ${isVisible['tabs'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                <img src={current.image} alt={current.title} className="w-[72%] h-auto max-h-72 md:max-h-96 object-contain" />
              </div>
              <div>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-purple-900 mb-5 leading-tight">{current.title}</h2>
                <p className="text-black/50 leading-relaxed text-base mb-8">{current.description}</p>
                <div className="w-20 h-1 bg-yellow-500 rounded-full" />
              </div>
            </div>

            {/* Details Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              <div className="bg-purple-50 rounded-2xl p-8 border border-purple-100 card-lift-purple-accent hover:bg-white">
                <div className="w-12 h-12 rounded-xl bg-teal-500 flex items-center justify-center mb-5">
                  <i className="ri-lightbulb-line text-yellow-400 text-xl" />
                </div>
                <h3 className="font-serif text-lg font-semibold text-purple-900 mb-5">{t('programs.methodology')}</h3>
                <ul className="space-y-3">
                  {current.methodology.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-black/50 text-sm">
                      <i className="ri-check-line text-yellow-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-purple-50 rounded-2xl p-8 border border-purple-100 card-lift-purple-accent hover:bg-white">
                <div className="w-12 h-12 rounded-xl bg-teal-500 flex items-center justify-center mb-5">
                  <i className="ri-award-line text-yellow-400 text-xl" />
                </div>
                <h3 className="font-serif text-lg font-semibold text-purple-900 mb-5">{t('programs.benefits')}</h3>
                <ul className="space-y-3">
                  {current.benefits.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-black/50 text-sm">
                      <i className="ri-check-line text-yellow-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-purple-50 rounded-2xl p-8 border border-purple-100 card-lift-purple-accent hover:bg-white">
                <div className="w-12 h-12 rounded-xl bg-teal-500 flex items-center justify-center mb-5">
                  <i className="ri-palette-line text-yellow-400 text-xl" />
                </div>
                <h3 className="font-serif text-lg font-semibold text-purple-900 mb-5">{t('programs.activities')}</h3>
                <ul className="space-y-3">
                  {current.activities.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-black/50 text-sm">
                      <i className="ri-check-line text-yellow-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}